import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useRef, useImperativeHandle, Fragment } from "react";
import assert from "assert";
import { Field } from "../private/Field/Field.mjs";
import { useDatePicker } from "@react-aria/datepicker";
import { useDatePickerState } from "@react-stately/datepicker";
import { Calendar } from "./Calendar.mjs";
import { Box } from "../Box/Box.mjs";
import { getLocalTimeZone, CalendarDate } from "@internationalized/date";
import { useField } from "@react-aria/label";
import { Input } from "./Input.mjs";
import { ActionField } from "./ActionField.mjs";
import "../Text/TextContext.mjs";
import "../Heading/HeadingContext.mjs";
import "../../css/lineHeightContainer.css.mjs";
import "../../css/reset/reset.css.mjs";
import "../../css/atoms/sprinkles.css.mjs";
import "../../hooks/useIcon/icon.css.mjs";
import "../../css/typography.css.mjs";
import { IconCalendar } from "../icons/IconCalendar/IconCalendar.mjs";
import { useDefaultMessages } from "../useDefaultMessages/useDefaultMessages.mjs";
function dateToCalendarDate(date) {
  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
}
const getLabelProps = (props) => {
  if ("label" in props) return { label: props.label };
  if ("aria-label" in props) return { label: props["aria-label"] };
  if ("aria-labelledby" in props) return { labelId: props["aria-labelledby"] };
  return {};
};
const DateField = forwardRef((props, ref) => {
  var _a;
  const containerRef = useRef(null);
  const localTimeZone = getLocalTimeZone();
  const value = props.value ? dateToCalendarDate(props.value) : props.value;
  assert(
    !((props == null ? void 0 : props.minDate) && (props == null ? void 0 : props.maxDate)) || props.minDate <= props.maxDate,
    "maxDate must be after minDate"
  );
  const formattedProps = {
    id: props.id,
    value,
    minValue: props.minDate ? dateToCalendarDate(props.minDate) : void 0,
    maxValue: props.maxDate ? dateToCalendarDate(props.maxDate) : void 0,
    isDisabled: props.disabled,
    shouldForceLeadingZeros: true,
    isReadOnly: props.readOnly,
    isDateUnavailable: props.shouldDisableDate ? (date) => props.shouldDisableDate(date.toDate(localTimeZone)) : void 0,
    onChange: (date) => {
      props.onChange((date == null ? void 0 : date.toDate(localTimeZone)) ?? null);
    },
    onFocus: props.onFocus,
    onBlur: props.onBlur,
    ...getLabelProps(props)
  };
  const datePickerState = useDatePickerState(formattedProps);
  const {
    groupProps,
    fieldProps: inputProps,
    buttonProps,
    calendarProps,
    dialogProps
  } = useDatePicker(formattedProps, datePickerState, containerRef);
  const { defaultMessages } = useDefaultMessages();
  const field = useField({
    ...props
  });
  useImperativeHandle(ref, () => ({
    focus: () => {
      datePickerState.setOpen(true);
    },
    close: () => {
      datePickerState.setOpen(false);
    }
  }));
  function onCalendarClose() {
    var _a2;
    datePickerState.close();
    (_a2 = props == null ? void 0 : props.onClose) == null ? void 0 : _a2.call(props);
  }
  return /* @__PURE__ */ jsx(
    Field,
    {
      ...props,
      ...field,
      value: (_a = props.value) == null ? void 0 : _a.toString(),
      labelId: void 0,
      alwaysShowSecondaryIcon: true,
      secondaryIcon: /* @__PURE__ */ jsx(
        ActionField,
        {
          id: buttonProps.id,
          icon: /* @__PURE__ */ jsx(IconCalendar, {}),
          label: defaultMessages.DateField.actionFieldLabel,
          onAction: (e) => {
            if (buttonProps == null ? void 0 : buttonProps.onPress) buttonProps.onPress(e);
          },
          "aria-describedby": buttonProps["aria-describedby"],
          "aria-expanded": buttonProps["aria-expanded"],
          "aria-label": buttonProps["aria-label"],
          "aria-labelledby": buttonProps["aria-labelledby"],
          disabled: buttonProps.isDisabled
        }
      ),
      children: (overlays, fieldProps, icon, secondaryIcon, prefix) => /* @__PURE__ */ jsxs(Fragment, { children: [
        icon,
        prefix,
        /* @__PURE__ */ jsx(
          Box,
          {
            ...fieldProps,
            disabled: props.disabled,
            padding: "xxsmall",
            width: "full",
            ref: containerRef,
            children: /* @__PURE__ */ jsx(
              Box,
              {
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                gap: "xsmall",
                ...groupProps,
                children: /* @__PURE__ */ jsx(Input, { fieldProps: inputProps })
              }
            )
          }
        ),
        overlays,
        secondaryIcon,
        datePickerState.isOpen && /* @__PURE__ */ jsx(Box, { ...dialogProps, children: /* @__PURE__ */ jsx(
          Calendar,
          {
            inputRef: containerRef,
            calendarProps,
            onClose: onCalendarClose
          }
        ) })
      ] })
    }
  );
});
export {
  DateField,
  dateToCalendarDate
};
